import { QuickscreenOfferResponse } from '@weave/schema-gen-ts/dist/schemas/payments-platform/care-credit-integration/care_credit_integration.pb';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { ContactType_Enum } from '@weave/schema-gen-ts/dist/shared/persons/v3/enums.pb';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useQuery, UseQueryOptions } from 'react-query';
import { usOptions, canadaOptions } from '@frontend/geography';
import { SchemaCareCreditIntegrationV1Service } from '../v1/service';

const allStatesMap = new Map([...usOptions, ...canadaOptions].map((state) => [state.label, state.value]));

dayjs.extend(utc);

type ContactType = 'MOBILE' | 'BUSINESS' | 'HOME';
type PhoneType = 'PHONE_MOBILE' | 'PHONE_WORK' | 'PHONE_HOME';
export const convert = {
  personPhoneToContactType: <T = ContactType_Enum>(type: T): ContactType => {
    switch (type) {
      case ContactType_Enum.PHONE_HOME:
        return 'HOME';
      case ContactType_Enum.PHONE_MOBILE:
        return 'MOBILE';
      case ContactType_Enum.PHONE_WORK:
        return 'BUSINESS';
      default:
        return 'HOME';
    }
  },
  contactTypeToPersonPhone: (type: ContactType | string): PhoneType => {
    switch (type) {
      case 'HOME':
        return ContactType_Enum.PHONE_HOME;
      case 'MOBILE':
        return ContactType_Enum.PHONE_MOBILE;
      case 'BUSINESS':
        return ContactType_Enum.PHONE_WORK;
      default:
        return ContactType_Enum.PHONE_HOME;
    }
  },
};

export const getPatientQueryFields = (person: Person | undefined) => {
  if (!person) {
    return null;
  }
  const email = person?.contactInfo?.find((contact) => contact.type === 'EMAIL')?.destination || '';

  const phone = ['PHONE_MOBILE', 'PHONE_HOME', 'PHONE_WORK']
    .map((type) => {
      return person.contactInfo?.find((contact) => contact.type === type && contact.destination);
    })
    .filter(Boolean)[0];

  const contactNumber = phone?.destination || '';
  const contactType = phone?.type ? convert.personPhoneToContactType(phone.type) : 'HOME';

  const dateOfBirth = person.birthdate ? dayjs(person.birthdate).utc().format('YYYYMMDD') : '';

  let state = person.address?.state || '';
  if (state.length > 2) {
    state = allStatesMap.get(state) || '';
  }

  const parsedData = {
    firstName: person.firstName || '',
    lastName: person.lastName || '',
    email,
    dateOfBirth,
    contactNumber,
    contactType,
    addressLine1: person.address?.street || '',
    city: person.address?.city || '',
    state,
    zipCode: person.address?.postalCode || '',
  };

  return parsedData;
};

export const getCareCreditPatientDataQueryKey = (
  person: Person | undefined,
  locationId?: string,
  merchantNumber?: string
) => {
  return [
    'careCreditPatientData',
    locationId ?? '',
    merchantNumber ?? '',
    person?.personId ?? '',
    /**
     * Removed person?.modified at.
     * Was causing a duplicate query in the payments panel, as the v1:v3 conversion lacks the modifiedAt field.
     * It technically exists on the v1 instance, but it it's null, when it is set directly from the v3 endpoints.
     */
  ];
};

type UseCareCreditPatientDataQueryProps = {
  locationId: string | undefined;
  merchantNumber: string | undefined;
  person: Person | undefined;
};
export const useCareCreditPatientDataQuery = (
  { merchantNumber, person, locationId }: UseCareCreditPatientDataQueryProps,
  options: UseQueryOptions<
    QuickscreenOfferResponse,
    unknown,
    QuickscreenOfferResponse,
    ReturnType<typeof getCareCreditPatientDataQueryKey>
  > = {}
) => {
  const query = useQuery({
    ...options,
    queryKey: getCareCreditPatientDataQueryKey(person, locationId, merchantNumber),
    queryFn: () => {
      return SchemaCareCreditIntegrationV1Service.QuickscreenOffer({
        locationId,
        merchantNumber,
        personId: person?.personId,
      });
    },
    enabled: options.enabled !== false && !!locationId && !!merchantNumber && !!person?.personId,
  });

  return {
    query,
  };
};
